// DoctorsPage.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Phone, Search, Filter, User } from "lucide-react";
import SEO from "./SEO";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const WP_API_BASE = "https://pranaam.pranaamhospitals.in/wp-json/wp/v2";
const PRIORITY_IDS = [29, 31, 32, 33];
const PLACEHOLDER_IMAGE = "/placeholder.svg?height=300&width=300";

export default function DoctorsPage() {
  const doctorsSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaam.pranaamhospitals.in/",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad with a team of highly experienced doctors and specialists.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/doctors"
    },
    "headline": "Meet Our Doctors at Pranaam Hospitals",
    "description": "Explore the profiles of expert doctors and medical specialists at Pranaam Hospitals providing patient-centric healthcare in Hyderabad.",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "doctors in Hyderabad",
      "best doctors at Pranaam Hospitals",
      "medical specialists Hyderabad",
      "multi specialty doctors Hyderabad"
    ],
    "articleSection": [
      "Cardiology",
      "Neurology",
      "Orthopedics",
      "Gynecology",
      "Pediatrics",
      "General Medicine"
    ]
  }
];

  const [doctors, setDoctors] = useState([]);
  const [categories, setCategories] = useState([{ id: "all", name: "All Departments" }]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch categories
  useEffect(() => {
  let cancelled = false;

  async function fetchCategories() {
    try {
      const res = await fetch(`${WP_API_BASE}/categories?per_page=100`);
      const data = await res.json();
      if (cancelled) return;

      // IDs TO EXCLUDE
      const EXCLUDED_IDS = [44, 42, 311, 33, 36, 1];

      // Filter categories
      const filtered = data.filter((c) => !EXCLUDED_IDS.includes(c.id));

      const mapped = [
        { id: "all", name: "All Departments" },
        ...filtered.map((c) => ({ id: c.id, name: c.name })),
      ];

      setCategories(mapped);
    } catch (err) {
      console.error("Categories fetch error:", err);
    }
  }

  fetchCategories();
  return () => { cancelled = true; };
}, []);


  // Fetch doctors
  useEffect(() => {
    let cancelled = false;
    async function fetchDoctors() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${WP_API_BASE}/doctors?per_page=100&_embed=1`);
        if (!res.ok) throw new Error(`Failed to fetch doctors: ${res.status}`);

        const data = await res.json();
        if (cancelled) return;

        const mapped = data.map((post) => {
          const name = post.title?.rendered || "";
          const slug = post.slug || "";
          const designation = post.acf?.designation || "";
          const specialty = post.acf?.latest_achievement || "";
          const categoryIds = Array.isArray(post.categories) ? post.categories : [];

          let categoryNames = [];
          if (post._embedded?.["wp:term"]) {
            const flatTerms = post._embedded["wp:term"].flat();
            categoryNames = flatTerms
              .filter((t) => t.taxonomy === "category")
              .map((t) => t.name);
          }

          let image = PLACEHOLDER_IMAGE;
          if (post._embedded?.["wp:featuredmedia"]?.[0]) {
            const media = post._embedded["wp:featuredmedia"][0];
            image = media.source_url || PLACEHOLDER_IMAGE;
          }

          return {
            id: post.id,
            name,
            slug,
            designation,
            specialty,
            categoryIds,
            categoryNames,
            image,
          };
        });

        const priorityMap = new Map();
        PRIORITY_IDS.forEach((id, index) => priorityMap.set(id, index));

        const priorityFound = [];
        const others = [];

        mapped.forEach((doc) => {
          if (priorityMap.has(doc.id)) priorityFound.push(doc);
          else others.push(doc);
        });

        priorityFound.sort((a, b) => priorityMap.get(a.id) - priorityMap.get(b.id));
        others.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

        setDoctors([...priorityFound, ...others]);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError(err.message || "Unknown error");
          setLoading(false);
        }
      }
    }
    fetchDoctors();
    return () => { cancelled = true; };
  }, []);

  // Filter doctors
  const filteredDoctors = doctors.filter((doctor) => {
    const matchesSearch =
      doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.designation.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesDepartment =
      selectedDepartment === "all" ||
      doctor.categoryIds.includes(Number(selectedDepartment));

    return matchesSearch && matchesDepartment;
  });

  return (
    <>
  <SEO
    title="Our Doctors | Expert Medical Specialists at Pranaam Hospitals Hyderabad"
    description="Meet the experienced doctors and medical specialists at Pranaam Hospitals. Our expert team delivers compassionate, advanced, and patient-centric healthcare."
    canonical="https://pranaam.pranaamhospitals.in/doctors"
    robots="index, follow"
    author="Pranaam Hospitals"
    publisher="Pranaam Hospitals"
    keywords="Pranaam Hospitals doctors, best doctors in Hyderabad,
              specialist doctors Hyderabad, cardiologists Hyderabad,
              neurologists Hyderabad, orthopedic doctors Hyderabad,
              gynecologists Hyderabad, pediatricians Hyderabad"
    schema={doctorsSchema}
  />
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero */}
        <section className="py-8 bg-white">
          <div className="container px-4 mx-auto">
            <h1 className="mb-6 text-3xl font-bold text-center text-primary">Doctors</h1>
          </div>
        </section>

        {/* Search + Filter */}
        <section className="py-4 bg-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search doctors by name or specialty"
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter className="text-primary h-4 w-4" />
                <span className="text-sm font-medium">Filter by:</span>
                <Select
                  value={selectedDepartment}
                  onValueChange={(value) => setSelectedDepartment(value)}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>

                  <SelectContent>
                    {categories.map((dept) => (
                      <SelectItem key={dept.id} value={String(dept.id)}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Doctors Grid (ALL DOCTORS SHOWN – NO LOAD MORE) */}
        <section className="py-8 bg-white">
          <div className="container px-4 mx-auto">
            {loading ? (
              <div className="p-8 text-center bg-gray-50 rounded-lg">Loading doctors…</div>
            ) : error ? (
              <div className="p-8 text-center bg-red-50 rounded-lg text-red-700">Error: {error}</div>
            ) : filteredDoctors.length === 0 ? (
              <div className="p-8 text-center bg-gray-50 rounded-lg">
                <User className="mx-auto mb-4 text-gray-400 h-12 w-12" />
                <h3 className="mb-2 text-xl font-bold">No Doctors Found</h3>
                <p className="text-gray-600">Try different keywords or filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {filteredDoctors.map((doctor) => (
                  <div key={doctor.id} className="flex flex-col items-center p-4 border rounded-lg">
                    <div className="relative w-full h-64 mb-4 overflow-hidden">
                      <img
                        src={doctor.image || PLACEHOLDER_IMAGE}
                        alt={doctor.name}
                        className="object-cover w-full h-full"
                      />
                    </div>

                    <h3 className="mb-2 text-xl font-bold text-center text-primary">{doctor.name}</h3>
                    <p className="mb-2 text-sm text-center text-gray-700">{doctor.designation}</p>

                    <Button variant="default" className="bg-primary hover:bg-primary/90" asChild>
                      <Link to={`/doctors/${doctor.slug}`}>View Profile</Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-12 bg-primary text-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              <div className="text-center md:text-left">
                <h2 className="mb-2 text-2xl font-bold md:text-3xl">Need Medical Assistance?</h2>
                <p>Schedule an appointment with our specialists today.</p>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button variant="secondary" size="lg" className="gap-2 shadow-md" asChild>
                  <Link to="/book-appointment">
                    <Calendar className="w-5 h-5" /> Book an Appointment
                  </Link>
                </Button>

                <Button variant="secondary" size="lg" className="gap-2 shadow-md" asChild>
                  <a href="tel:+919966060000">
                    <Phone className="w-5 h-5" /> Call Us Now
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
    </>
  );
}
